<template>
    <div class="relative">
        <OverLay :show="loading_stockist" :absolute="true" :transparent="true"></OverLay>
        <div class="title-bar">
            <h4>{{ title }}</h4>
            <a class="link" @click="changeLocation">{{ link }}</a>
        </div>
        
        <div v-if="!display_location">
            <div 
                v-for="(option, index) in options" 
                :key="`option-${id}-`+index" 
                class="availability-option"
                :class="[
                    { active: selected_option === option.value }, 
                    { notallowed: !option.allowed }, 
                ]"
            >
                <label :ref="option.value">
                    <input 
                        @click="optionOnChange($event, option)" 
                        type="radio" 
                        :id="option.value" 
                        name="product-availability" 
                        :value="option.value" 
                        :checked="option.value === selected_option ? true: false"
                    />
                    <div class="content-wrapper relative">
                        <div v-if="option.value === 'delivery'" class="icon-wrapper"><SvgDelivery></SvgDelivery></div>
                        <div v-else-if="option.value === 'click-and-collect'" class="icon-wrapper"><SvgClickCollect></SvgClickCollect></div>
                        <div v-else-if="option.value === 'stockist'" class="icon-wrapper"><SvgStockist></SvgStockist></div>

                        <div class="text-wrapper">
                            <div>
                                <span>{{ option.title }}</span>
                                <span 
                                    class="stock-alert" 
                                    v-if="!option.allowed"
                                >
                                    Not Available
                                </span>
                                <div class="inline-block" v-if="option.value == 'click-and-collect' && !option.valid" >
                                    <a 
                                        class="link"
                                        @click.prevent="displayStockists"
                                    >
                                        Change Store
                                    </a>
                                    <ProductAvailabilityList 
                                        :stockists="stockist_list" 
                                        :show="custom_display_list"   
                                        @change-stockist="changeStockist"
                                        :id="id"
                                        :absolute="true"
                                    ></ProductAvailabilityList>
                                </div>
                            </div>
                            <div class="d-flex flex-column">
                                <span class="subtitle" v-if="option.subtitle" :class="[{none: selected_option == option.value}]">{{ option.subtitle }}</span>
                                <span 
                                    class="stock-alert" 
                                    v-if="option.value == 'click-and-collect' && option.allowed && !option.valid"
                                >
                                    Not Available for this stockist
                                </span>
                            </div>
                            <a 
                                v-show="selected_option === option.value && option.value === 'click-and-collect'" 
                                class="link"
                                :class="{ disabled: store_count == 0 }"
                                @click.prevent="onViewMoreStore"
                            >
                                {{ option_link }}
                            </a>
                        </div>
                    </div>
                </label>

                <ProductAvailabilityDetail
                    :name="name" 
                    :address="address"
                    :phone="phone"
                    :distance="distance"
                    :value="option.value"
                    :store_count="store_count"
                    :show="display_detail && selected_option == option.value && selected_option != 'delivery'"        
                    @view-more-store="onViewMoreStore"
                >
                </ProductAvailabilityDetail>
                <ProductAvailabilityList 
                    :stockists="stockist_list" 
                    :full_list="full_list" 
                    :value="option.value"
                    :show="display_list && selected_option == option.value"   
                    @change-stockist="changeStockist"
                    :id="id"
                ></ProductAvailabilityList>
            </div>
        </div>
        
        <div v-else class="availability-option">
            <label class="stockist">
                <span>Enter your suburb or postcode to find your closest stockist</span>
                <div class="relative">
                    <input type="text" placeholder="e.g. 4000, Brisbane" v-model="search" @keyup="searchAddresses"/>
                    <SvgSearch :show="!searching"></SvgSearch>
                    <div class="spinner" v-show="searching">
                        <div class="rect1"></div>
                        <div class="rect2"></div>
                        <div class="rect3"></div>
                        <div class="rect4"></div>
                        <div class="rect5"></div>
                    </div>
                </div>

                <ul class="address-list" v-show="display_addresses">
                    <li v-if="addresses.length === 0">Postcode / Suburb Not Found</li>
                    <li 
                        v-for="(address, index) in addresses" 
                        :key="`address-${address.postcode}-${address.locality}-`+index"
                        @click="searchStockists(address.postcode, address.locality, address.state)"
                    >{{ address.locality }}, {{ address.postcode }}, {{ address.state }}</li>
                </ul>
            </label>
            
            <ProductAvailabilityList 
                :stockists="stockist_list" 
                :show="display_list"
                @change-stockist="changeStockist"
                :id="id"
                :show_search_address="false"
                :preview="true"
            ></ProductAvailabilityList>
        </div>
    </div>
</template>
  
<script>
    import SvgClickCollect from './SvgClickCollect.vue';
    import SvgDelivery from './SvgDelivery.vue';
    import SvgStockist from './SvgStockist.vue';
    import ProductAvailabilityDetail from './ProductAvailabilityDetail.vue';
    import ProductAvailabilityList from './ProductAvailabilityList.vue';
    import OverLay from './OverLay.vue';
    import SvgSearch from './SvgSearch.vue';
    import axios from 'axios';
    import debounce from "lodash.debounce";

    const PRODUCT_AVAILABILITY = "Product Availability";
    const FIND_A_STOCKIST = "Find a Stockist";
    const CHANGE_MY_LOCATION = "Change My Location";
    const BACK = "Back";
    const CHANGE_STORE = "Change Store";
    const CANCEL = "Cancel";

    const AJAX_URL = '/wp-admin/admin-ajax.php';
    axios.defaults.headers.post['Content-Type'] = 'multipart/form-data';

    export default {
        name: 'ProductAvailability',
        components: {
            SvgClickCollect,
            SvgDelivery,
            SvgStockist,
            SvgSearch,
            ProductAvailabilityDetail,
            ProductAvailabilityList,
            OverLay
        },
        props: {
            type: String,
            preferred: Number,
            product_id: Number,
            postcode: String,
            suburb: String,
            state: String
        },
        data: function () {
            return {
                options: [
                    { title: 'Home Delivery', value: 'delivery' },
                    { title: 'Click and Collect', value: 'click-and-collect' },
                    { title: 'Find A Stockist', value: 'stockist' }
                ],
                selected_option: '',
                name: '',
                phone: '',
                address: '',
                distance: '',
                store_count: 0,
                display_list: false,
                custom_display_list: false,
                display_detail: false,
                display_location: false,
                display_addresses: false,
                id: '',
                stockist_list: [],
                full_list: [],
                addresses: [],
                title: PRODUCT_AVAILABILITY,
                link: CHANGE_MY_LOCATION,
                option_link: CHANGE_STORE,
                search: '',
                prev_search: '',
                searching: false,
                selected_suburb: '',
                loading_stockist: true
            }
        },
        methods: {
            optionOnChange: function (e, option) {
                console.log('optionOnChange');

                var selected = option.value;
                var paDom = document.getElementById("selected-product-availability");
                var { type } = this;

                if (
                    (selected == 'click-and-collect' && type === 'delivery') ||
                    (selected == 'delivery' && type != 'delivery' && type != 'all') ||
                    (selected == 'click-and-collect' && !option.valid) ||
                    !option.allowed
                ) {
                    // don't allow user to click it
                    e.preventDefault();
                    return;
                }

                if (paDom) {
                    if (selected === 'stockist') {
                        if (type === 'all') paDom.value = 'delivery'; // default to delivery if all option available
                        else paDom.value = type;
                    } else {
                        paDom.value = selected;
                    }
                }
                
                this.selected_option = selected;
                this.display_list = false;
                this.custom_display_list = false;
                this.display_detail = true;
            },
            changeStockist: function (id) {
                console.log('changeStockist');

                this.display_list = false;
                this.display_detail = true;
                this.custom_display_list = false;
                const self = this;
                const { display_location } = this;

                if (display_location) {
                    this.changeLocation();
                }

                this.search = "";
                var selected = this.stockist_list.find(e => e.id == id);

                const stockist = document.getElementById("selected-stockist");

                if (stockist) {
                    stockist.value = id;
                }

                document.querySelector("pp-overlay").setAttribute( 'show', "true");

                /**
                 * Update the selected store in WC session & refresh the side cart fragment
                 */
                axios.post(AJAX_URL, {
                    action: 'ajax_cart_change_stockist',
                    key: 'selected-store',
                    value: selected.id
                }).then(function (response) {
                    const{ stockist_changed, postcode, state, suburb } = response.data;

                    if (!stockist_changed) {
                        self.id = id;
                        self.name = selected.name;
                        self.address = selected.address;
                        self.phone = selected.phone;
                        self.distance = selected.distance;
                        
                        self.setDefaultOptionBasedOnType(selected);
                        self.getShippingQuote(self.product_id, state, postcode, suburb);

                        // refresh the stockists 
                        axios.post(AJAX_URL, {
                            action: 'ajax_get_stockist',
                            postcode: postcode,
                            locality: suburb,
                            state: state,
                            product_id: self.product_id
                        })
                        .then(function (response) {
                            const { stockists } = response.data; 

                            let stockist_list = self.trimStockists(stockists, null);
                            self.stockist_list = stockist_list;
                        })
                    }
                    
                    console.log("before update get_refreshed_fragments");

                    axios.post('/?wc-ajax=get_refreshed_fragments').then(function (response) {
                        console.log("after update get_refreshed_fragments");
                        const cart_content = document.querySelector('div.widget_shopping_cart_content');

                        if (cart_content) {
                            cart_content.outerHTML = response.data['fragments']['div.widget_shopping_cart_content'];
                        }
                        console.log("before set attribute for pp-overlay");
                        document.querySelector("pp-overlay").setAttribute( 'show', "false");
                        console.log("after set attribute for pp-overlay");

                    })
                })

            },
            onViewMoreStore: function () {
                console.log('onViewMoreStore');

                this.display_list = !this.display_list;
                this.display_detail = !this.display_detail;
            },
            searchStockists: function(postcode, locality, state) {
                const self = this;
                const { preferred } = self;

                // hide addresses list
                self.display_addresses = false;
                this.search = `${locality}, ${postcode}, ${state}`;
                this.selected_suburb = this.capitalize(locality);

                if (postcode != '') {
                    self.searching = true;

                    axios.post(AJAX_URL, {
                        action: 'ajax_get_stockist',
                        postcode: postcode,
                        locality: locality,
                        state: state,
                        product_id: self.product_id
                    })
                    .then(function (response) {
                        const { stockists } = response.data; 
                        const _preferred = stockists.find(s => s.id == preferred);

                        let stockist_list = self.trimStockists(stockists, _preferred);
                        self.stockist_list = stockist_list;
                        self.display_list = true;
                        self.display_detail = false;
                        self.searching = false;
                    })
                    .catch(function (error) {
                        console.log(error);
                    });    
                } else {
                    self.display_addresses = false;
                }
            },
            searchAddresses: debounce(function () {
                const self = this;
                const { search, prev_search } = self;

                // search address if user input more than 3 characters
                if (search.length >= 3 && search != prev_search) {
                    self.searching = true;
                    self.prev_search = search;

                    axios.post(AJAX_URL, {
                        action: 'ajax_get_address',
                        address: search,
                    })
                    .then(function (response) {
                        const { addresses } = response.data;

                        self.addresses = addresses;
                        self.searching = false;
                        // show addresses list
                        self.display_addresses = true;

                        if (addresses.length === 0) {
                            setTimeout(function () {
                                self.display_addresses = false;
                            }, 3000);
                        }
                    })
                    .catch(function (error) {
                        console.log(error);
                    });   
                } else if (search.length === 0) {
                    // clear address list
                    self.display_addresses = false;
                    self.addresses = [];
                }
            }, 500),
            displayStockists: function () {
                this.custom_display_list = !this.custom_display_list;
            },
            trimStockists: function (stockists, _preferred) {
                let stockist_list = stockists;
                const { type } = this;

                if (!_preferred) {
                    _preferred = stockist_list[0];
                } else {
                    // COMMENT THIS SECTION AS IT IS NOT SHOWING NEAREST STOCKIST
                    // const foundIdx = stockists.findIndex(el => el.id == _preferred.id);
                    // stockist_list.splice(foundIdx, 1);
                    // stockist_list.unshift(_preferred);
                }
                
                if (type === 'click-and-collect') {
                    stockist_list = stockist_list.filter((e) => e.clickcollect == true || e.id == _preferred.id);
                }

                return stockist_list.slice(0, 3);

            },
            setDefaultOptionBasedOnType: function (selected) {
                const { type } = this;
                let allow_collect = false;
                let paDom = document.getElementById("selected-product-availability");

                if (selected) {
                    allow_collect = selected.clickcollect;
                }
                /**
                 *  Default the option when component is rendered / changed
                 *  Hide the add to cart button if it is not allow to purchase
                 */ 
                if (type === 'catalogue' || !selected || (type === 'click-and-collect' && !allow_collect)) {
                    this.selected_option = 'stockist'; 
                } else {
                    if (type === 'all') {
                        // default to delivery
                        this.selected_option = 'delivery';
                        paDom.value = 'delivery';
                    } else {
                        this.selected_option = type;
                        paDom.value = type;
                    }
                }
            },
            changeLocation: function () {
                let { display_location, selected_suburb } = this;

                display_location = !display_location;
                this.display_location = display_location;
                this.title = !display_location ? PRODUCT_AVAILABILITY : FIND_A_STOCKIST;
                this.link = !display_location ? CHANGE_MY_LOCATION + `: ${selected_suburb}`  : BACK;
                this.display_list = false;

                if (!display_location) {
                    this.search = "";
                    this.display_detail = true;
                }
            },
            updateSubtitle: function (price = 0) {
                const { id, stockist_list, type } = this;
                var { options } = this;
                const _preferred = stockist_list.find(s => s.id == id);
                let clickcollect = false;
                const buybtn = document.querySelector(".single_add_to_cart_button");
                let isBuyBtnEnabled = false;

                if (_preferred) {
                    clickcollect = _preferred['clickcollect'];
                }

                /**
                 * Add the quote and distance to the options
                 */
                for (var i = 0; i < options.length; i++) {
                    const method = options[i]['value'];

                    if (method === 'delivery') {
                        if (price > 0) {
                            options[i]['subtitle'] = 'This item ships from $' + price.toFixed(2) + ' to your door';
                        }
                    } else {
                        if (_preferred) {
                            options[i]['subtitle'] = _preferred['name'] + ' (' + _preferred['distance'] + 'km)';
                        }
                    }

                    if (type === 'all') {
                        options[i]['allowed'] = true; // always allow all options
                    } else if (type === 'catalogue') {
                        options[i]['allowed'] = false; // always disallow all options

                        if (method === 'stockist') {
                            options[i]['allowed'] = true; // always allow stockist
                        }
                    } else {
                        /**
                         * Always allow Find a Stockist option
                         * Show Click and Collect when it is Click and Collect only product
                         * Show Delivery when it is Delivery only product
                         */
                        if (method === 'stockist' || method === type) {
                            options[i]['allowed'] = true; // always allow delivery
                        }
                    }

                    // valid - use to check if the stockist available for click and collect
                    if (method === 'click-and-collect') {
                        options[i]['valid'] = clickcollect;
                    } else {
                        options[i]['valid'] = true;
                    }

                    // Either delivery / click and collect is valid, enable the buy button
                    if (method != 'stockist' && options[i]['valid'] === true && options[i]['allowed'] === true) {
                        isBuyBtnEnabled = true;
                    }

                    console.log(options[i]);
                }

                if (buybtn) buybtn.disabled = !isBuyBtnEnabled;
                this.options = options;
            },
            getShippingQuote: function (product_id, state, postcode, suburb) {
                console.log('getShippingQuote');

                const self = this;

                if (self.type === 'click-and-collect') {
                    self.updateSubtitle();
                    return;
                }

                /**
                 * Fire an ajax call to Shippit API to get quote for product
                 */
                axios.post(AJAX_URL, {
                    action: 'ajax_get_shipping_quote',
                    id: product_id,
                    state: state,
                    postcode: postcode,
                    suburb: suburb
                })
                .then(function (response) {
                    const { price } = response.data; 
                    
                    self.updateSubtitle(price);
                })
                .catch(function (error) {
                    console.log(error);
                });
            },
            capitalize: function (str) {
                const arr = str.toLowerCase().split(" ");
                
                for (let i = 0; i < arr.length; i++) {
                    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
                }
                
                return arr.join(" ");
            }
        },
        mounted() {
            const self = this;
            var { preferred, product_id, state, postcode, suburb } = self; 
            const buybtn = document.querySelector(".single_add_to_cart_button");

            console.log('mounted ' + preferred);

            if (buybtn) buybtn.disabled = true;

            axios.post(AJAX_URL, {
                action: 'ajax_get_stockist',
                postcode: postcode,
                locality: suburb,
                state: state,
                product_id: product_id
            })
            .then(function (response) {
                const { stockists } = response.data; 
                let _preferred = stockists.find(s => s.id == preferred);

                // default to nearest stockists
                if (!_preferred) { _preferred = stockists[0]; }

                let stockist_list = self.trimStockists(stockists, _preferred);
                
                self.full_list = stockists.slice(0, 3);
                self.stockist_list = stockist_list;
                self.loading_stockist = false;
                self.store_count = stockist_list.length - 1;
                self.name = _preferred.name;
                self.address = _preferred.address;
                self.phone = _preferred.phone;
                self.distance = _preferred.distance;
                self.id = _preferred.id;
                self.display_detail = true;

                var stockist = document.getElementById("selected-stockist");
            
                if (stockist) {
                    stockist.value = _preferred.id;
                }

                self.selected_suburb = self.capitalize(suburb);
                self.link = CHANGE_MY_LOCATION + `: ${self.selected_suburb}`;
                self.setDefaultOptionBasedOnType(_preferred);
                self.getShippingQuote(product_id, state, postcode, suburb);
            })
            .catch(function (error) {
                console.log(error);
            });  
        }, 
        watch: { 
            preferred: function(newVal, oldVal) {
                // update this compoent when side cart changes store
                if (newVal != oldVal) {
                    const id = newVal;
                    const selected = this.stockist_list.find(e => e.id == id);
                    this.name = selected.name;
                    this.address = selected.address;
                    this.phone = selected.phone;
                    this.distance = selected.distance;
                    this.id = id;

                    var stockist = document.getElementById("selected-stockist");
                    
                    if (stockist) {
                        stockist.value = id;
                    }

                    this.updateSubtitle();
                    this.setDefaultOptionBasedOnType(selected);
                }
            },
            stockists: function (newVal, oldVal) {
                if (newVal != oldVal) {
                    var { stockists } = this;
                    this.stockist_list = stockists = JSON.parse(stockists);
                }
            },
            display_list: function (newVal) {
                if (newVal) {
                    this.option_link = CANCEL;
                } else {
                    this.option_link = CHANGE_STORE;
                }
            }
        }
    }
</script>
  
<style lang="scss">
    @import "@/assets/global.scss";

   .title-bar {
        display: flex;
        justify-content: space-between;

        h4 {
            color: $pp-grey80;
        }

        a {
            margin: auto 0;
        }
    }

    .availability-option {
        display: flex;
        flex-direction: column;
        border: 1px solid #D3D2D2;
        border-radius: 4px;
        margin-bottom: 10px;
        padding: 20px;

        label {
            display: flex;
            cursor: pointer;
            position: relative;

            &.stockist {
                cursor: auto;
                flex-direction: column;

                &-active {
                    flex-direction: row;
                }

                span {
                    color: #7B7979;
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 22px;
                }
            }
        }

        &.active {
            border: 1px solid $pp-blue;
            background: rgba(241, 246, 252, 0.55);
        }

        &.notallowed {
            opacity: 0.5;
            cursor: not-allowed;

            label {
                pointer-events: none;
            }
        }

        .content-wrapper {
            display: flex;
            flex: 1 1 0;

            .icon-wrapper, .text-wrapper {
                padding: 10px;

                a {
                    display: block;
                }
            }

            .icon-wrapper {
                display: inline-block;
            }

            .text-wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                
                .subtitle {
                    color: $pp-grey60;
                    font-size: 14px;
                    font-weight: 500;
                    margin-right: 10px;
                }

                .inline-block {
                    margin-left: 10px;
                }

                .relative .stock-alert {
                    margin-left: 10px;
                }
            }
        }

        .more-options {
            margin: 0px auto 10px auto;
            text-align: center;
            display: block;
        }

        .stockist {
            .relative {
                input {
                    width: -webkit-fill-available;
                    height: 30px;
                    border-radius: 2px;
                    border: 1px solid #D3D2D2;
                    padding: 5px 20px;
                    margin-top: 20px;
                    font-size: 16px;
                }

                svg {
                    position: absolute;
                    top: 30px;
                    right: 15px;
                    width: 23px;
                    height: 23px;
                    cursor: pointer;
                }

                .spinner {
                    position: absolute;
                    top: 30px;
                    right: 15px;
                    height: 23px;
                    margin: 0;
                }

                .spinner > div {
                    background-color: #ACAAAB;
                }
            }
        }
    }

    .stock-alert {
        background: #FFE8EA;
        color: #FF2F61;
        padding: 2px 5px;
        border-radius: 46px;
        font-size: 12px;
        width: fit-content;
        text-wrap: nowrap;
    }

    .address-list {
        position: absolute;
        top: 100%;
        left: 0;
        background: $white;
        border: 1px solid #D3D2D2;
        width: 100%;
        list-style: none;
        padding: 0;
        margin: 0;
        z-index: 999;
        max-height: 250px;
        overflow-y: scroll;
    
        li {
            padding: 10px 20px;
            border-bottom: 1px solid #D3D2D2;
            cursor: pointer;

            &:hover {
                background: #EBF2F8;
            }
        }
    }
</style>